import { API_ENDPOINT, AI_ENDPOINT, WEBHOOK_ENDPOINT } from '@Env/variables';
import {
  promptAnalyzer,
  TicketInput,
  TicketClassificationResponse,
  AnalyzerWebhookReport,
  SelectedTicketEntry,
  AnalyzerPodReport,
  AnalyzerMappings,
  AnalyzerWeeklyPrompt,
} from '@Models/types';
import { createAxiosInstance } from './axios';

export const analyzerReportsAPI = (logout: () => void) => {
  const axios = createAxiosInstance(logout, WEBHOOK_ENDPOINT);

  const analyzer = {
    getMappings: async (): Promise<AnalyzerMappings[]> => {
      const { data } = await axios.get('/api/analyzer/getMappings');
      return data;
    },
    updateMappings: async (mappings: AnalyzerMappings) => {
      const { data } = await axios.post('/api/analyzer/updateMappings', {
        ...mappings,
      });
      return data;
    },
    deleteMapping: async (username: string) => {
      const { data } = await axios.post('/api/analyzer/deleteMapping', {
        username,
      });
      return data;
    },

    getPrompts: async (): Promise<AnalyzerWeeklyPrompt[]> => {
      const { data } = await axios.get('/api/analyzer/prompts');
      return data;
    },

    createPrompt: async (
      prompt: Omit<
        AnalyzerWeeklyPrompt,
        '_id' | 'authorEmail' | 'dateCreated' | 'isDefault'
      >
    ) => {
      const { data } = await axios.post('/api/analyzer/prompts', prompt);
      return data;
    },

    updatePrompt: async (
      originalTitle: string,
      originalAuthor: string,
      prompt: Partial<AnalyzerWeeklyPrompt>
    ) => {
      const { data } = await axios.post('/api/analyzer/prompts/update', {
        originalTitle,
        originalAuthor,
        ...prompt,
      });
      return data;
    },

    deletePrompt: async (promptTitle: string, authorEmail: string) => {
      const { data } = await axios.post('/api/analyzer/prompts/delete', {
        promptTitle,
        authorEmail,
      });
      return data;
    },

    setDefaultPrompt: async (promptTitle: string, authorEmail: string) => {
      const { data } = await axios.post('/api/analyzer/prompts/setDefault', {
        promptTitle,
        authorEmail,
      });
      return data;
    },
  };

  return analyzer;
};

export const analyzerAPI = (logout: () => void) => {
  const axios = createAxiosInstance(logout, API_ENDPOINT);

  const analyzer = {
    getAnalyzerReports: async (): Promise<AnalyzerWebhookReport[]> => {
      const { data } = await axios.get('/api/getAnalyzerReports');
      return data;
    },

    getAnalyzerWeeklyReports: async (
      podId: string
    ): Promise<AnalyzerPodReport[]> => {
      const { data } = await axios.get('/api/getAnalyzerWeeklyReports', {
        params: { podId },
      });
      return data;
    },

    tagAnalyzerReports: async (
      selectedTickets: Record<string, SelectedTicketEntry>
    ) => {
      const { data } = await axios.post('/api/tagAnalyzerReports', {
        selectedTickets,
      });
      return data;
    },

    getAnalyzerPrompts: async (): Promise<promptAnalyzer[]> => {
      const { data } = await axios.post('/api/analyzerPrompt', {
        method: 'GET',
      });
      return data.prompts;
    },

    addAnalyzerPrompt: async (
      prompt: promptAnalyzer
    ): Promise<promptAnalyzer> => {
      const { data } = await axios.post('/api/addAnalyzerPrompt', {
        ...prompt,
      });
      return data.prompt;
    },

    updateAnalyzerPrompt: async (
      prompt: promptAnalyzer
    ): Promise<promptAnalyzer> => {
      const { data } = await axios.post('/api/updateAnalyzerPrompt', {
        ...prompt,
      });
      return data.prompt;
    },

    deleteAnalyzerPrompt: async (promptId: string): Promise<void> => {
      await axios.post('/api/deleteAnalyzerPrompt', {
        id: promptId,
      });
    },
    //internal call
    editAnalyzerPrompt: async (
      prompt: promptAnalyzer
    ): Promise<promptAnalyzer[]> => {
      await analyzer.updateAnalyzerPrompt(prompt);
      const refreshedPrompts = await analyzer.getAnalyzerPrompts();
      return refreshedPrompts;
    },
    evaluateReport: async (
      week: string,
      podId: string,
      evaluated: boolean
    ): Promise<void> => {
      await axios.post('/api/evaluateReport', {
        week,
        podId,
        evaluated,
      });
    },
  };
  return analyzer;
};

export const analyzerAiAPI = (logout: () => void) => {
  const axios = createAxiosInstance(logout, AI_ENDPOINT);

  return {
    classifyTickets: async (
      tickets: TicketInput[],
      selectedPrompt?: promptAnalyzer | null
    ): Promise<TicketClassificationResponse> => {
      const { data } = await axios.post('/api/classifyTickets', {
        tickets,
        selectedPrompt,
      });
      return data;
    },
  };
};
